import type { OptionCallPut } from '../types';
import { useOptionsSymbolSearchService } from './useSymbolSearchService';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getSymbolSearchServiceQuery, selectExactMatches } from 'src/queries/symbolSearchService';

export function useIsOptionsSymbolValid({
    symbolType,
    underlyingSymbol,
    expirationDate,
    optionCallPut,
    strikePrice,
}: {
    symbolType: 'equity' | 'options';
    underlyingSymbol?: string;
    expirationDate?: string;
    optionCallPut?: OptionCallPut;
    strikePrice?: number;
}) {
    const { symbolSearchService } = useOptionsSymbolSearchService();

    const validExactMatchSelector = useMemo(
        () =>
            selectExactMatches({
                underlyingSymbol,
                expirationDate,
                optionCallPut,
                strikePrice,
            }),
        [underlyingSymbol, expirationDate, optionCallPut, strikePrice],
    );

    const { data: matchingOptionSymbols, isLoading: isLoadingMatchingOptionSymbols } = useQuery({
        ...getSymbolSearchServiceQuery(underlyingSymbol ?? '', symbolSearchService),
        select: validExactMatchSelector,
    });

    const doesExist = useMemo(() => {
        // not validating equity symbols, as the SymbolInput already handles this
        // equity symbols only have a SymbolInput, making this additional layer redundant
        if (symbolType === 'equity') {
            return true;
        }

        // we only attempt to validate once all fields are filled
        if (!underlyingSymbol || !expirationDate || strikePrice === undefined || !optionCallPut) {
            return true;
        }

        return !isLoadingMatchingOptionSymbols && matchingOptionSymbols && matchingOptionSymbols.length > 0;
    }, [
        symbolType,
        underlyingSymbol,
        expirationDate,
        strikePrice,
        optionCallPut,
        isLoadingMatchingOptionSymbols,
        matchingOptionSymbols,
    ]);

    return Boolean(doesExist);
}
